import DOMPurify from "dompurify";

/** Povolené tagy — přesně ta podmnožina, kterou umí produkovat náš Tiptap editor. */
const ALLOWED_TAGS = [
  "p",
  "br",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "s",
  "h2",
  "h3",
  "ul",
  "ol",
  "li",
  "a",
  "blockquote",
  "code",
  // YouTube embed z editoru: <div data-youtube-video><iframe src="…/embed/ID"></iframe></div>
  "div",
  "iframe",
];

/** Jediné domény, ze kterých pouštíme <iframe>. */
const YOUTUBE_EMBED_HOSTS = ["www.youtube-nocookie.com", "www.youtube.com"];

/**
 * True jen pro adresu přehrávače YouTube. Whitelist je záměrně úzký (host +
 * cesta `/embed/ID`) — obecný iframe by z admin popisu udělal libovolný cizí
 * rámec uvnitř našeho layoutu (phishing/clickjacking).
 *
 * Kontrolujeme přes `URL`, ne přes prefix stringu: parser normalizuje `..`,
 * takže „/embed/../../redirect?q=zlo" spadne na `/redirect` a neprojde.
 */
function isYoutubeEmbedSrc(src: string): boolean {
  let url: URL;
  try {
    url = new URL(src);
  } catch {
    return false;
  }
  return (
    url.protocol === "https:" &&
    YOUTUBE_EMBED_HOSTS.includes(url.hostname) &&
    /^\/embed\/[\w-]+$/.test(url.pathname)
  );
}

/** Bloky, mezi které při stripování vkládáme mezeru, ať se text neslepí. */
const BLOCK_SELECTOR = "p, h2, h3, li, blockquote";

/**
 * Vyčistí HTML od nebezpečných prvků (script, on* handlery, javascript: URL…)
 * a povolí jen formátovací tagy z editoru. Volat VŽDY před vykreslením HTML
 * z DB na stránku — obsah sice zadává admin, ale bez sanitizace by uložený
 * `<script>` byl stored XSS na všechny návštěvníky webu.
 *
 * Odkazy a YouTube embedy pak dořešíme lokálně nad naparsovaným výsledkem —
 * bez globálního DOMPurify hooku (ten by HMR mohl zdvojit a ovlivnil by každou
 * jinou sanitizaci v appce).
 */
export function sanitizeRichHtml(html: string): string {
  const clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR: [
      "href",
      // Atributy YouTube iframu (src se navíc ověřuje proti allowlistu níž).
      // `allow` ani `frameborder` Tiptap negeneruje → nepovolujeme je vůbec.
      "src",
      "width",
      "height",
      "allowfullscreen",
      // Wrapper embedu — ALLOW_DATA_ATTR je false, takže musí být vyjmenovaný.
      "data-youtube-video",
    ],
    // Bez tohohle by DOMPurify hodnoty „640"/„true" proháněl přísným
    // ALLOWED_URI_REGEXP níž a atributy zahodil (mizel by fullscreen).
    ADD_URI_SAFE_ATTR: ["width", "height", "allowfullscreen"],
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
    // Jen bezpečná schémata odkazů (aplikuje se na URI atributy, tj. href/src).
    ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|tel:|#|\/)/i,
  });

  // Vyčištěný výstup přeparsujeme (bezpečné, DOMParser nic nespouští) a odkazům
  // natvrdo dáme target=_blank + rel=noopener… → žádný reverse-tabnabbing.
  const doc = new DOMParser().parseFromString(clean, "text/html");
  for (const a of doc.body.querySelectorAll("a[href]")) {
    a.setAttribute("target", "_blank");
    a.setAttribute("rel", "noopener noreferrer nofollow");
  }

  // DOMPurify hlídá jen schéma URL, ne doménu — iframe s jinou než YouTube
  // adresou proto zahodíme i s wrapperem. Dokument z DOMParseru nemá browsing
  // context, takže se do téhle chvíle žádný rámec nenačte.
  for (const iframe of doc.body.querySelectorAll("iframe")) {
    if (!isYoutubeEmbedSrc(iframe.getAttribute("src") ?? "")) {
      (iframe.closest("[data-youtube-video]") ?? iframe).remove();
    }
  }
  return doc.body.innerHTML;
}

/**
 * Převede HTML na prostý text (bez tagů). Pro náhledy v kartách (line-clamp)
 * a SEO meta description. Mezi bloky/`<br>` vkládá mezeru, jinak by se sousední
 * odstavce slepily („<p>A</p><p>B</p>" → „A B"). DOMParser HTML neprovádí,
 * takže je bezpečný i na nedůvěryhodný vstup.
 */
export function stripHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  for (const br of doc.body.querySelectorAll("br")) br.replaceWith(" ");
  for (const block of doc.body.querySelectorAll(BLOCK_SELECTOR)) block.append(" ");
  return (doc.body.textContent ?? "").replace(/\s+/g, " ").trim();
}

/**
 * Prostý text napsaný v e-mailovém dialogu → HTML odstavce. Text se escapuje,
 * aby omylem nefungoval jako markup; `{{proměnné}}` escape nechává být, takže
 * je server pořád umí dosadit. Text, který už HTML obsahuje (předvyplněná
 * šablona), vracíme beze změny.
 */
export function plainTextToHtml(text: string): string {
  if (text.includes("<")) return text;
  return text
    .split(/\n{2,}/)
    .map(
      (odstavec) =>
        `<p>${odstavec
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/\n/g, "<br/>")}</p>`,
    )
    .join("");
}

/** True, když je HTML prázdné/jen bílé znaky (Tiptap prázdný obsah = „<p></p>"). */
export function isBlankHtml(html: string | null | undefined): boolean {
  if (!html) return true;
  // Embed žádný text nenese, ale obsah to je. Bez téhle výjimky by popis
  // složený jen z videa spadl pod „prázdný" a sekce by se na webu neukázala.
  const doc = new DOMParser().parseFromString(html, "text/html");
  if (doc.body.querySelector("[data-youtube-video], iframe")) return false;
  return stripHtml(html).length === 0;
}
